import React, {useState, Suspense, useEffect, useCallback, useRef} from 'react';
import Header from "./Header";
import Error from "../error/Error";
import ConnectionService from "../../services/ConnectionService";
import Guacamole from "guacamole-common-js";
import TokenService from "../../services/TokenService";
import ErrorPage from "../error/ErrorPage";
import {FullScreen, useFullScreenHandle} from "react-full-screen";
import Loading from "../Loading";
import tunnel from "../config/TunnelState";
import Reconnect from "../Reconnect";

function Layout() {
    const [errorNotification, setErrorNotification] = useState(null);
    const [activeSession, setActiveSession] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [httpStatusCode, setHttpStatusCode] = useState();
    const [errorMessage, setErrorMessage] = useState();
    const [connectionState, setConnectionState] = useState();
    const handle = useFullScreenHandle();
    const [connectionHost, setConnectionHost] = useState(null);
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    const timeOutFunctionId = useRef(null);
    const fullscreen = useRef(false);
    const displayRef = useRef(null);
    const client = useRef(null);
    const linuxPasteKeySequence = [
        65511,
        118
    ]

    const windowsPasteKeySequence = [
        65507,
        118
    ]

    const updateSize = () => {
        const headerSpace = 54
        const width = window.innerWidth;
        let height = fullscreen.current ? window.innerHeight : window.innerHeight - headerSpace

        console.log(`Updating Guacamole width: ${width} px height ${height} px`)
        client.current.sendSize(width, height);
    }

    const attachDisplay = useCallback(() => {
        console.log("Attaching Display")
        if (displayRef.current !== null) {
            displayRef.current.appendChild(client.current.getDisplay().getElement());
            console.log("Display created")
        }
    }, [])

    const reportChange = useCallback((state) => {
        fullscreen.current = state
        console.log('Fullscreen Mode Enabled ' + fullscreen.current);
    }, []);

    const parentOnClickHandler = () => {
        const window = document.getElementById('flex-window')
        window.focus()
        displayRef.current.focus();
        const fileUpload = document.getElementById('customFile');
        if (fileUpload === document.activeElement) {
            fileUpload.blur()
        }
    };

    const onDropFile = (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log("dropped file")
    }

    const dragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }

    const dragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }

    const onFullScreen = () => {
        handle.enter().then(() => { console.log("Entering Fullscreen Mode") })
    }

    const onEndSession = () => {
        setActiveSession(false)
        client.current.disconnect()
        displayRef.current.removeChild(client.current.getDisplay().getElement())
        client.current = undefined
    }

    const setApiError = (errorResponse) => {
        console.log(errorResponse)
        if (errorResponse.data) {
            setHttpStatusCode(errorResponse.status);
            if (errorResponse.data.errorMessage) {
                setErrorMessage(errorResponse.data.errorMessage);
            }
        }
        setLoaded(true);
    }

    /** Guacamole States
     * "CONNECTING": 0
     * "OPEN": 1
     * "CLOSED": 2
     * "UNSTABLE" : 3
     */
    const setupEvents = useCallback((reconnect) => {
        client.current.onstatechange = function(clientState) {
            switch (clientState) {
                case 0:
                    setConnectionState("Idle")
                    break;
                case 1:
                    setConnectionState("Connecting")
                    break;
                case 2:
                    setLoaded(true);
                    setConnectionState("Connected, waiting for first update...")
                    break;
                case 3:
                    setLoaded(true);
                    const width = document.getElementById('flex-window').clientWidth
                    const height = window.innerHeight - 54
                    changeWindowHeight(width,height)
                    console.log("Connection Success")
                    setConnectionState("Connected")
                    setActiveSession(true)

                    window.addEventListener("resize", function() {
                        clearTimeout(timeOutFunctionId.current);
                        timeOutFunctionId.current = setTimeout(updateSize, 500);
                    });

                    attachDisplay()
                    attachMouse()

                    if (!reconnect) {
                        attachKeyboard()
                    }

                    client.current.onclipboard = function(stream, mimetype) {
                        if (!navigator.clipboard) {
                            console.log("Clipboard cannot be read")
                            return;
                        }
                        if (/^text\//.exec(mimetype)) {
                            const stringReader = new Guacamole.StringReader(stream);
                            let json = "";
                            stringReader.ontext = function ontext(text) {
                                json += text
                            }
                            stringReader.onend = function() {
                                navigator.clipboard.writeText(json).then(function() {
                                    console.log('Async: Copying to clipboard was successful! ' + JSON.stringify(json));
                                }, function(err) {
                                    console.error('Async: Could not copy text: ', err);
                                });
                            }
                        }
                    };
                    client.current.onfile = function clientFileReceived(stream, mimetype, filename) {
                        console.log("Stream: ", stream)
                        console.log("Mime type: ", mimetype)
                        console.log("File name: ", filename)

                        let blobReader = new Guacamole.BlobReader(stream, mimetype);

                        blobReader.onprogress = function onprogress(length) {
                            console.log("Blob length: ", length)
                        }

                        blobReader.onend = function onend() {
                            console.log("Creating download link for : ", filename)
                            const url = window.URL.createObjectURL(new Blob([blobReader.getBlob()]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            window.URL.revokeObjectURL(url);
                            link.remove();
                        }
                        // Send ack or the download won't start
                        stream.sendAck("OK", 0x0000);
                    }
                    break;
                case 4:
                    setConnectionState("Disconnecting")
                    break;
                case 5:
                    setLoaded(true);
                    setActiveSession(false)
                    setConnectionState("Disconnected")
                    break;
                default:
                    setActiveSession(false)
                    setConnectionState("Unknown");
            }
        };

        client.current.onerror = function(error) {
            if (tunnel.getGuacamoleStatus(error.code)) {
                setErrorNotification(tunnel.getGuacamoleStatus(error.code).text)
            } else {
                setErrorNotification(JSON.stringify(error))
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[attachDisplay])

    const attachKeyboard = () => {
        let linuxUserInput = new Array(linuxPasteKeySequence.length);
        let windowsUserInput = new Array(windowsPasteKeySequence.length);
        let keyboard = new Guacamole.Keyboard(document)
        console.log("Keyboard Attached")
        keyboard.onkeydown = function (keysym) {
            linuxUserInput = [ ...linuxUserInput.slice(1), keysym ];
            windowsUserInput = [ ...windowsUserInput.slice(1), keysym ];
            console.log(keysym)
            if (linuxPasteKeySequence.every((v,k) => v === linuxUserInput[ k ]) || windowsPasteKeySequence.every((v,k) => v === windowsUserInput[ k ])) {
                console.log("Paste Sequence Detected")
                const stream = client.current.createClipboardStream("text/plain");
                const writer = new Guacamole.StringWriter(stream);
                navigator.clipboard.readText()
                    .then(text => {
                        for (let i = 0; i < text.length; i += 4096) {
                            console.log("writing to clipboard " + text.substring(i, i + 4096))
                            writer.sendText(text)
                        }
                        writer.sendEnd();
                    })
                    .catch(err => {
                        console.error('Failed to read clipboard contents: ', err);
                    });
                setTimeout(() => {
                    client.current.sendKeyEvent(1, 65507);
                    client.current.sendKeyEvent(1, 118);
                    client.current.sendKeyEvent(0, 65507);
                    client.current.sendKeyEvent(0, 118);
                }, 1000);
            } else {
                client.current.sendKeyEvent(1, keysym);
            }
        };

        keyboard.onkeyup = function (keysym) {
            client.current.sendKeyEvent(0, keysym);
        };
    }

    const attachMouse = () => {
        let mouse = (new Guacamole.Mouse(displayRef.current));
        mouse.onmousedown =
          mouse.onmouseup   =
            mouse.onmousemove = function(mouseState) {
                client.current.sendMouseState(mouseState);
            };

        console.log("Mouse Attached")
    }

    const verifyToken = useCallback(() => {
        console.log("verify token")
        TokenService.connect(token)
            .then(response => {
                setConnectionHost(response.data)
                try {
                    client.current = new Guacamole.Client(new Guacamole.WebSocketTunnel(ConnectionService.getWebsocketUri()))
                    client.current.connect()
                    setupEvents(false)
                } catch(e) {
                    setErrorNotification(e)
                    console.log('error connecting to websocket ', e);
                }
            })
            .catch(err => {
                if (err !== undefined) {
                    setApiError(err)
                } else {
                    setLoaded(true)
                    setErrorNotification("Error establishing connection to backend API")
                }
            });
    }, [setupEvents, token])

    const sendSize = useCallback(() => {
        if (document.getElementById('flex-window') !== null) {
            const width = document.getElementById('flex-window').clientWidth
            const height = window.innerHeight - 54
            ConnectionService.sendScreenSize({width: width, height: height})
              .then(() => {})
              .catch(err => {
                  if (err !== undefined) {
                      setApiError(err)
                  } else {
                      setLoaded(true)
                      setErrorNotification("Error establishing connection to backend API")
                  }
              });
        }
    }, [])

    useEffect(() => {
        verifyToken()
        sendSize()
        console.log("Rendered Guacamole Window")
    }, [verifyToken, sendSize]);

    const onReconnect = useCallback(() => {
        console.log("Attempting to reconnect session with token")
        TokenService.reconnect(token)
          .then(response => {
              setConnectionHost(response.data)
              try {
                  client.current = new Guacamole.Client(new Guacamole.WebSocketTunnel(ConnectionService.getWebsocketUri()))
                  client.current.connect()
                  setupEvents(true)
                  sendSize()
              } catch(e) {
                  setErrorNotification(e)
                  console.log('error connecting to websocket ', e);
              }
          })
          .catch(err => {
              if (err !== undefined) {
                  setApiError(err)
              } else {
                  setLoaded(true)
                  setErrorNotification("Error establishing connection to backend API")
              }
          });
    }, [setupEvents, token, sendSize])

    const changeWindowHeight = useCallback((width, height) => {
        client.current.sendSize(width, height)
        console.log("Changed window height to " + height + "px")
        console.log("Changed window width to " + width + "px")
    }, []);

    if (errorMessage && (httpStatusCode !== 409)) {
        return (
            <ErrorPage responseCode={httpStatusCode} message={errorMessage}/>
        )
    }

    return (
        <div className="c-wrapper">
            <Suspense fallback={<Loading/>}>
                <Header onEndSession={onEndSession} onFullScreen={onFullScreen} remoteHost={connectionHost} guac={client.current}/>
                <Error message={errorNotification} active={activeSession}/>
                {activeSession ? null :
                    <Reconnect message={`Your session has timed out, Connection state ${connectionState} please don't refresh the page. Click here to reconnect to your instance`} reconnect={() => onReconnect()}/>
                }
                <div id={"main-container"} className="container-fluid pl-0">
                    <div className="row">
                      <div id={"flex-window"} className="col-sm-12 flex-window">
                          { loaded ? null : <Loading/> }
                          <FullScreen handle={handle} onChange={reportChange}>
                              <div onDragOver={dragOver}
                                   onDragEnter={dragEnter}
                                   onDrop={onDropFile}
                                   id={"window-wrapper"}
                                   className={"window-wrapper"}
                                   ref={displayRef}
                                   onClick={parentOnClickHandler}
                              />
                          </FullScreen>
                      </div>
                    </div>
                </div>
            </Suspense>
        </div>
    )
}

export default Layout;
